import React, { useEffect, useState } from "react";
import CourseListing from "./CourseListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/CourseSlice";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddCourseWrapper from "../Add/AddCourseWrapper";
import EditCourseWrapper from "../Edit/EditCourseWrapper";
import {
  useChangeStatusCourseByIdMutation,
  useDeleteCourseByIdMutation,
  useGetAllCourseDataQuery,
} from "src/services/CourseService";
import { capitalizeFirstLetter } from "src/utils/helper/stringHelper";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const CourseListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isOpenAddDialog,
    isOpenEditDialog,
    isTableLoading,
  } = useSelector((state: RootState) => state.course);
  const [selectedCourseId, setSelectedCourseId] = useState<string>();
  const [deleteCourse] = useDeleteCourseByIdMutation();
  const [changeStatusCourse] = useChangeStatusCourseByIdMutation();

  const { data, isLoading, isFetching } = useGetAllCourseDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["courseName"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteCourse = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Course ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteCourse(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusCourse = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Course ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusCourse({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };
  const moduleName = "COURSE";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    {
      field: "courseName",
      headerName: "Course Name",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "courseCode",
      headerName: "Course Code",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "orderRank",
      headerName: "Order Rank",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "type",
      headerName: "Type",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{capitalizeFirstLetter(row.type)}</span>,
    },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "center",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = [];

        if (canEdit) {
          options.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedCourseId(row?._id);
            },
          });
        }
        if (canActive) {
          options.push({
            label: "Active/Deactive",
            onClick: () => {
              handleChangeStatusCourse(row?._id);
            },
          });
        }
        if (canDelete) {
          options.push({
            label: "Delete",
            onClick: () => {
              handleDeleteCourse(row?._id);
            },
          });
        }

        return <ATMMenu options={options} />;
      },
    },
  ];

  return (
    <>
      <div>
        <CourseListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddCourseWrapper onClose={() => dispatch(setIsOpenAddDialog(false))} />
      ) : null}
      {isOpenEditDialog ? (
        <EditCourseWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedCourseId={selectedCourseId || ""}
        />
      ) : null}
    </>
  );
};

export default CourseListingWrapper;
